/**
 * Client-side slug generation utility
 * Mirrors the backend SlugGenerator logic for real-time preview
 */

/**
 * Generates a URL-safe slug from input text
 * Handles Unicode characters (ä → a, ö → o, etc.) and special characters
 */
export function generateSlug(input: string, maxLength: number = 100): string {
  if (!input || input.trim().length === 0) {
    return ''
  }

  // Normalize unicode characters to decomposed form (ä → a + ¨)
  let normalized = input.normalize('NFD')
  
  // Remove diacritics (accent marks) and convert to lowercase
  let withoutDiacritics = ''
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i]
    // Skip non-spacing marks (diacritics like ¨, ´, `, etc.)
    // Unicode categories: https://unicode.org/reports/tr44/#General_Category_Values
    if (!/[\u0300-\u036f]/.test(char)) {
      withoutDiacritics += char
    }
  }
  
  withoutDiacritics = withoutDiacritics.toLowerCase()

  // Replace sequences of non-alphanumeric characters with single hyphens
  let slug = withoutDiacritics.replace(/[^a-z0-9]+/g, '-')
  
  // Remove leading and trailing hyphens
  slug = slug.replace(/^-+|-+$/g, '')
  
  // Limit length and ensure no trailing hyphen after truncation
  if (slug.length > maxLength) {
    slug = slug.substring(0, maxLength).replace(/-+$/, '')
  }

  // Fallback for empty results
  if (slug.length === 0) {
    return 'tenant'
  }
    
  return slug
}

/**
 * Checks if a tenant name already exists
 */
export function checkNameExists(name: string, existingNames: string[]): boolean {
  return existingNames.includes(name)
}

/**
 * Generates a unique slug by appending a counter if necessary
 */
export function ensureUniqueSlug(baseSlug: string, existingNames: string[]): string {
  if (!baseSlug) {
    baseSlug = 'tenant'
  }

  let candidate = baseSlug
  let counter = 1
  
  while (existingNames.includes(candidate)) {
    // Ensure the counter doesn't make the slug too long
    const suffix = `-${counter}`
    const maxBaseLength = 100 - suffix.length
    
    const truncatedBase = baseSlug.length > maxBaseLength 
      ? baseSlug.substring(0, maxBaseLength).replace(/-+$/, '')
      : baseSlug
        
    candidate = truncatedBase + suffix
    counter++
    
    // Safety check to prevent infinite loops
    if (counter > 1000) {
      throw new Error(`Could not generate unique slug for base '${baseSlug}' after 1000 attempts`)
    }
  }
  
  return candidate
}